import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import fedHolidays from '@18f/us-federal-holidays';
// Use UTC to avoid timezone issues when comparing dates
dayjs.extend(utc);
/**
 * Get federal holidays for the given year in YYYY-MM-DD format
 */
function getFederalHolidaysForYear(year) {
    const holidays = fedHolidays.allForYear(year, {
        shiftSaturdayHolidays: true,
        shiftSundayHolidays: true
    });
    return holidays.map((holiday) => dayjs.utc(holiday.date).format('YYYY-MM-DD'));
}
/**
 * Get all federal holidays for the relevant date range
 */
export function getHolidaysForDateRange(baseDate) {
    const baseYear = dayjs.utc(baseDate).year();
    // Get holidays for current year and next year to handle year boundaries
    const holidays = [
        ...getFederalHolidaysForYear(baseYear),
        ...getFederalHolidaysForYear(baseYear + 1)
    ];
    return new Set(holidays);
}
/**
 * Check if a date is a federal holiday
 */
export function isHoliday(date) {
    const holidays = getHolidaysForDateRange(date);
    const dateStr = dayjs.utc(date).format('YYYY-MM-DD');
    return holidays.has(dateStr);
}
/**
 * Check if a date is a business day (not weekend, not federal holiday)
 */
export function isBusinessDay(date, holidays) {
    // Use UTC to get consistent day of week
    const utcDate = date.utc();
    const dayOfWeek = utcDate.day();
    // Weekend check (0 = Sunday, 6 = Saturday)
    if (dayOfWeek === 0 || dayOfWeek === 6) {
        return false;
    }
    // Holiday check
    const dateStr = utcDate.format('YYYY-MM-DD');
    return !holidays.has(dateStr);
}
/**
 * Add business days to a date, excluding weekends and federal holidays
 *
 * @param date - The starting date
 * @param days - Number of business days to add
 * @returns The resulting date after adding business days
 */
export function addBusinessDays(date, days) {
    const holidays = getHolidaysForDateRange(date);
    // Use UTC to avoid timezone issues
    let current = dayjs.utc(date);
    let remaining = days;
    while (remaining > 0) {
        current = current.add(1, 'day');
        if (isBusinessDay(current, holidays)) {
            remaining--;
        }
    }
    return current.toDate();
}
/**
 * Add calendar days to a date (simple date addition, no weekend/holiday exclusion)
 *
 * @param date - The starting date
 * @param days - Number of calendar days to add
 * @returns The resulting date after adding calendar days
 */
export function addCalendarDays(date, days) {
    const result = dayjs.utc(date).add(days, 'day');
    return result.toDate();
}
/**
 * Subtract business days from a date, excluding weekends and federal holidays
 *
 * @param date - The starting date
 * @param days - Number of business days to subtract
 * @returns The resulting date after subtracting business days
 */
export function subtractBusinessDays(date, days) {
    const holidays = getHolidaysForDateRange(date);
    let current = dayjs.utc(date);
    let remaining = days;
    while (remaining > 0) {
        current = current.subtract(1, 'day');
        if (isBusinessDay(current, holidays)) {
            remaining--;
        }
    }
    return current.toDate();
}
